/**
 * Stream Options tab (extra fields) = the integration's `list_extra_camera_fields()` from
 * actuate_admin, served by `camera/{id}/extra_fields/`. Every field carries a
 * `type` (UIFieldType); until ENG-950 they were all strings, so the tab rendered
 * everything as a text input. Milestone motion is the first checkbox.
 */
export const isCheckboxStreamParameter = (type?: string): boolean =>
  type === "checkbox";

/**
 * Choice fields carry `options` as `[label, value]` pairs (actuate_admin
 * BaseField, same shape the onboarding wizard's choice fields use). The Genetec
 * stream picker (ENG-2043) is the first on this tab; its labels include each
 * stream's probed resolution, and value "" means Auto.
 */
export const isChoiceStreamParameter = (type?: string): boolean =>
  type === "choice";

export type StreamParameterOption = [string, string];

/** Per-camera Protocol on direct-url integrations (ENG-2044). */
export const isPerCameraProtocolField = (
  field?: string,
  label?: string
): boolean => field === "protocol" || label === "Protocol";

export const siteDefaultProtocolOptionLabel = (
  siteProtocol?: string | null
): string => {
  const normalized = (siteProtocol ?? "rtsp").trim() || "rtsp";
  return `Site default (${normalized.toUpperCase()})`;
};

export const relabelProtocolChoiceOptions = (
  options: StreamParameterOption[] | undefined,
  siteProtocol?: string | null
): StreamParameterOption[] =>
  (options ?? []).map(([label, value]) => {
    if (value === "" || value === null || value === undefined) {
      return [siteDefaultProtocolOptionLabel(siteProtocol), ""];
    }
    return [label, value];
  });

/** A stored null/undefined matches the "" (default / Auto) option. */
export const choiceValue = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

export const formatStreamParameterValue = (
  value: unknown,
  type?: string,
  options?: StreamParameterOption[],
  context?: { field?: string; label?: string; siteProtocol?: string | null }
): string => {
  if (isCheckboxStreamParameter(type)) {
    return value ? "On" : "Off";
  }
  if (isChoiceStreamParameter(type) && Array.isArray(options)) {
    const normalized = choiceValue(value);
    if (
      isPerCameraProtocolField(context?.field, context?.label) &&
      normalized === ""
    ) {
      return siteDefaultProtocolOptionLabel(context?.siteProtocol);
    }
    const displayOptions = isPerCameraProtocolField(
      context?.field,
      context?.label
    )
      ? relabelProtocolChoiceOptions(options, context?.siteProtocol)
      : options;
    const match = displayOptions.find(
      ([, optionValue]) => optionValue === normalized
    );
    if (match) {
      return match[0];
    }
  }
  return value !== null && value !== undefined ? String(value) : "--";
};

/**
 * Tooltip map for `TooltipTableHeadComponent`, keyed by field name.
 *
 * The integration supplies a `helperText` per field (e.g. Milestone's
 * "Only pull frames when Milestone reports motion. Requires a sync to apply.").
 * It rode along in the payload unused until ENG-1758 — the tab rendered only
 * the label, so the one explanation of what a parameter does never reached the
 * operator. Fields without helperText simply get no icon.
 */
export const buildStreamParameterTooltips = (
  extraFields: { field?: string; helperText?: string }[] = []
): Record<string, string> =>
  extraFields.reduce<Record<string, string>>((tooltips, field) => {
    if (field?.field && field.helperText) {
      tooltips[field.field] = field.helperText;
    }
    return tooltips;
  }, {});
